import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../../services/api';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import {
  FaFilePdf, FaSpinner, FaClock, FaMoneyBillWave,
  FaUsers, FaFilter, FaTimes, FaChevronDown
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AdminReports = () => {
  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters state
  const [filters, setFilters] = useState(null);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [selectedRoomGroups, setSelectedRoomGroups] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);

  // Dropdown visibility state
  const [openDropdown, setOpenDropdown] = useState(null);

  // Colors for pie chart
  const COLORS = {
    staff: '#3B82F6',
    laundry: '#8B5CF6'
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedYears, selectedMonths, selectedStaff, selectedRoomGroups, selectedRooms]);

  const fetchFilters = async () => {
    setFiltersLoading(true);
    try {
      const data = await reportsAPI.getFilters();
      setFilters(data);

      // Set default: current month and year
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      setSelectedMonths([currentMonth]);
      if (data.years.includes(currentYear)) {
        setSelectedYears([currentYear]);
      } else if (data.years.length > 0) {
        setSelectedYears([data.years[0]]);
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
      setError('Error al cargar los filtros');
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedYears.length > 0) params.years = selectedYears.join(',');
      if (selectedMonths.length > 0) params.months = selectedMonths.join(',');
      if (selectedStaff.length > 0) params.staff_ids = selectedStaff.join(',');
      if (selectedRoomGroups.length > 0) params.room_group_ids = selectedRoomGroups.join(',');
      if (selectedRooms.length > 0) params.room_ids = selectedRooms.join(',');

      const data = await reportsAPI.getDashboardSummary(params);
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Error al cargar los datos del reporte');
    } finally {
      setLoading(false);
    }
  }, [selectedYears, selectedMonths, selectedStaff, selectedRoomGroups, selectedRooms]);

  // Multi-select dropdown component
  const MultiSelectDropdown = ({ label, options, selected, onChange, valueKey = 'value', labelKey = 'label', isYears = false }) => {
    const isOpen = openDropdown === label;

    const toggleOption = (value) => {
      if (selected.includes(value)) {
        onChange(selected.filter(v => v !== value));
      } else {
        onChange([...selected, value]);
      }
    };

    const selectAll = () => {
      if (isYears) {
        onChange([...options]);
      } else {
        onChange(options.map(o => o[valueKey]));
      }
    };

    const clearAll = () => {
      onChange([]);
    };

    const getDisplayText = () => {
      if (selected.length === 0) return `Seleccionar ${label}`;
      if (selected.length === 1) {
        if (isYears) return selected[0];
        const option = options.find(o => o[valueKey] === selected[0]);
        return option ? option[labelKey] : selected[0];
      }
      return `${selected.length} seleccionados`;
    };

    return (
      <div className="relative">
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        <button
          type="button"
          onClick={() => setOpenDropdown(isOpen ? null : label)}
          className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
            {getDisplayText()}
          </span>
          <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="flex justify-between p-2 border-b bg-gray-50">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Seleccionar todo
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Limpiar
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {(isYears ? options : options).map((option) => {
                const value = isYears ? option : option[valueKey];
                const optionLabel = isYears ? option : option[labelKey];
                const isSelected = selected.includes(value);

                return (
                  <label
                    key={value}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOption(value)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{optionLabel}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedYears([]);
    setSelectedMonths([]);
    setSelectedStaff([]);
    setSelectedRoomGroups([]);
    setSelectedRooms([]);
  };

  // Export functions
  const exportToPDF = () => {
    if (!dashboardData) return;

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text('Reporte de Costes', 14, 22);

    doc.setFontSize(10);
    doc.text(`Período: ${dashboardData.start_date} - ${dashboardData.end_date}`, 14, 32);

    autoTable(doc, {
      startY: 40,
      head: [['Concepto', 'Valor']],
      body: [
        ['Coste de Lavandería', `${dashboardData.laundry_cost}€`],
        ['Limpiezas realizadas', dashboardData.laundry_cleanings_count],
        ['Horas Contratadas', dashboardData.contracted_hours],
        ['Horas Reales', dashboardData.real_hours],
        ['Diferencia Horas', `${dashboardData.hours_difference} (${dashboardData.hours_difference_percent}%)`],
        ['Tiempo Medio/Habitación', `${dashboardData.avg_cleaning_time_minutes.toFixed(0)} min`],
        ['Habitaciones Limpiadas', dashboardData.total_rooms_cleaned],
        ['Coste Personal', `${dashboardData.total_staff_cost}€`],
        ['Coste Lavandería', `${dashboardData.total_laundry_cost}€`],
        ['Coste Total', `${dashboardData.total_cost}€`],
      ],
    });

    doc.save(`reporte_costes_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (filtersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const pieData = dashboardData ? [
    { name: 'Personal', value: dashboardData.total_staff_cost, color: COLORS.staff },
    { name: 'Lavandería', value: dashboardData.total_laundry_cost, color: COLORS.laundry }
  ] : [];

  const hasActiveFilters = selectedYears.length > 0 || selectedMonths.length > 0 ||
    selectedStaff.length > 0 || selectedRoomGroups.length > 0 || selectedRooms.length > 0;

  return (
    <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Reportes y Estadísticas</h1>
        <p className="text-sm sm:text-base text-gray-600">
          Análisis de costes y rendimiento del establecimiento
        </p>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
                Limpiar filtros
              </button>
            )}
            <button
              onClick={exportToPDF}
              disabled={!dashboardData}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
            >
              <FaFilePdf />
              PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {filters && (
            <>
              <MultiSelectDropdown
                label="Año"
                options={filters.years}
                selected={selectedYears}
                onChange={setSelectedYears}
                isYears={true}
              />
              <MultiSelectDropdown
                label="Mes"
                options={filters.months}
                selected={selectedMonths}
                onChange={setSelectedMonths}
              />
              <MultiSelectDropdown
                label="Personal"
                options={filters.staff}
                selected={selectedStaff}
                onChange={setSelectedStaff}
              />
              <MultiSelectDropdown
                label="Grupo"
                options={filters.room_groups}
                selected={selectedRoomGroups}
                onChange={setSelectedRoomGroups}
              />
              <MultiSelectDropdown
                label="Habitación"
                options={filters.rooms}
                selected={selectedRooms}
                onChange={setSelectedRooms}
              />
            </>
          )}
        </div>

        {dashboardData && (
          <div className="mt-3 text-xs text-gray-500">
            Datos desde {dashboardData.start_date} hasta {dashboardData.end_date}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <FaSpinner className="animate-spin text-4xl text-blue-500" />
        </div>
      ) : dashboardData ? (
        <>
          {/* Three Main Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Laundry Cost Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FaMoneyBillWave className="text-purple-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600">Factura Lavandería</h3>
                  <p className="text-xs text-gray-400">Coste estimado de sábanas y toallas</p>
                </div>
              </div>
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {dashboardData.laundry_cost.toFixed(2)}€
              </div>
              <div className="text-sm text-gray-500">
                {dashboardData.laundry_cleanings_count} limpiezas realizadas
              </div>
            </div>

            {/* Hours Comparison Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FaClock className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600">Horas Contratadas vs Reales</h3>
                  <p className="text-xs text-gray-400">Comparación de tiempo trabajado</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Contratadas:</span>
                  <span className="font-semibold text-gray-800">{dashboardData.contracted_hours.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Reales:</span>
                  <span className="font-semibold text-gray-800">{dashboardData.real_hours.toFixed(1)}h</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Diferencia:</span>
                    <span className={`font-bold ${dashboardData.hours_difference >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {dashboardData.hours_difference >= 0 ? '+' : ''}{dashboardData.hours_difference.toFixed(1)}h
                      <span className="text-xs ml-1">({dashboardData.hours_difference_percent.toFixed(1)}%)</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Average Cleaning Time Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <FaClock className="text-green-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600">Tiempo Medio de Limpieza</h3>
                  <p className="text-xs text-gray-400">Por habitación</p>
                </div>
              </div>
              <div className="text-3xl font-bold text-green-600 mb-2">
                {dashboardData.avg_cleaning_time_minutes > 60
                  ? `${Math.floor(dashboardData.avg_cleaning_time_minutes / 60)}h ${Math.round(dashboardData.avg_cleaning_time_minutes % 60)}m`
                  : `${Math.round(dashboardData.avg_cleaning_time_minutes)} min`
                }
              </div>
              <div className="text-sm text-gray-500">
                {dashboardData.total_rooms_cleaned} habitaciones limpiadas
              </div>
            </div>
          </div>

          {/* Cost Distribution Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pie Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Distribución del Coste de Limpieza
              </h3>
              {dashboardData.total_cost > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={90}
                        innerRadius={50}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={2}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={2} stroke="#fff" />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `${value.toFixed(2)}€`}
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <div className="font-bold text-xl text-blue-600">{dashboardData.staff_cost_percent.toFixed(0)}%</div>
                      <div className="text-sm text-gray-600">Personal</div>
                      <div className="text-xs text-gray-500">{dashboardData.total_staff_cost.toFixed(2)}€</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                      <div className="font-bold text-xl text-purple-600">{dashboardData.laundry_cost_percent.toFixed(0)}%</div>
                      <div className="text-sm text-gray-600">Lavandería</div>
                      <div className="text-xs text-gray-500">{dashboardData.total_laundry_cost.toFixed(2)}€</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No hay datos de costes para el período seleccionado
                </div>
              )}
            </div>

            {/* Staff Cost Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FaUsers className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Coste Estimado Total de Personal</h3>
                  <p className="text-sm text-gray-500">Basado en horas fichadas y tarifa por hora</p>
                </div>
              </div>

              <div className="text-center py-8">
                <div className="text-5xl font-bold text-blue-600 mb-2">
                  {dashboardData.total_staff_cost.toFixed(2)}€
                </div>
                <div className="text-gray-500">
                  {dashboardData.real_hours.toFixed(1)} horas trabajadas
                </div>
              </div>

              <div className="border-t pt-4 mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Coste por hora medio:</span>
                  <span className="font-semibold text-gray-800">
                    {dashboardData.real_hours > 0
                      ? `${(dashboardData.total_staff_cost / dashboardData.real_hours).toFixed(2)}€/h`
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Coste por habitación:</span>
                  <span className="font-semibold text-gray-800">
                    {dashboardData.total_rooms_cleaned > 0
                      ? `${(dashboardData.total_staff_cost / dashboardData.total_rooms_cleaned).toFixed(2)}€`
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Coste total (Personal + Lavandería):</span>
                  <span className="font-bold text-green-600 text-lg">
                    {dashboardData.total_cost.toFixed(2)}€
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminReports;
